import { useTranslator } from "@eo-locale/react";
import { boolean } from "yup";
import { ChangeEvent } from "react";

import type { Question } from "../question";
import {
  Field,
  HTMLWrapper,
  Checkbox,
  VisuallyHidden
} from "@/storybook-components";

export const validate = async (
  answer: string[],
  question: Question
): Promise<string[]> => {
  let schema = boolean();
  if (question.required) {
    schema = schema.required();
  }
  try {
    await schema.validate(answer);
    return [];
  } catch (err: any) {
    return err.errors;
  }
};

function CheckBoxQuestion(props: Question): JSX.Element {
  const {
    id,
    question,
    required,
    hasChecked,
    error,
    status,
    onChangeHandler,
    notes,
    hasLabel
  } = props;
  const { translate } = useTranslator();

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    if (onChangeHandler) onChangeHandler(event.target.checked);
  };

  return (
    <Field.Root
      my={4}
      id={`${id}`}
      invalid={error?.length > 0 || false}
      required={required || false}
      disabled={status === "saving"}
      className={error?.length > 0 ? "ehqQuestionError" : ""}
      css={{
        "& .chakra-checkbox__control": { marginTop: 1 },
        "& .chakra-checkbox__label": {
          marginTop: "auto",
          "& > span": { fontWeight: "normal" }
        }
      }}
    >
      {hasLabel && (
        <Field.Label as="legend">
          <HTMLWrapper html={question} />
          {required && (
            <VisuallyHidden>{`* ${translate(
              "common.required"
            )}`}</VisuallyHidden>
          )}
        </Field.Label>
      )}

      <Checkbox
        onCheckedChange={(details) =>
          handleChange({
            target: { checked: details.checked === true }
          } as ChangeEvent<HTMLInputElement>)
        }
        checked={hasChecked}
        size={{ base: "lg", xl: "md" }}
        disabled={status === "saving"}
        alignItems="baseline"
      >
        <HTMLWrapper html={question} />
        {required && (
          <VisuallyHidden>{`${translate("common.required")}`}</VisuallyHidden>
        )}
      </Checkbox>
      {error?.length > 0 && (
        <Field.ErrorText data-testid="checkboxQuestion-errorMessage">
          <Field.ErrorIcon />
          {translate(`errors.${error}`)}
        </Field.ErrorText>
      )}
      <Field.HelperText>{notes}</Field.HelperText>
    </Field.Root>
  );
}

export { CheckBoxQuestion };
